import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App as AntApp, Button } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { usePushPrompt } from '../context/PushPromptContext';
import { AsyncButton } from './kit';
import { useMemoGateOpen } from '../lib/memoGateStore';

/**
 * PushPrompt — the soft ask for notification permission, shown just after sign-in.
 *
 * Says what will actually be sent before spending the browser's one-shot
 * permission dialog on it. "Not now" is free: it re-arms in 30 days and the
 * Notifications screen keeps its own Enable button in the meantime. See
 * PushPromptContext for why the two-step ask matters.
 */

const Icon = ({ children }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

// What push is actually for, in the agent's own terms. Concrete beats "stay
// updated" — these are the three things they would be annoyed to hear late.
const REASONS = [
  {
    icon: <Icon><path d="M4 20h4l10-10a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M13.5 6.5l3 3" /></Icon>,
    title: 'The moment a customer signs',
    sub: 'Booking forms and co-share agreements, as they come back',
  },
  {
    icon: <Icon><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5h3.5a1.6 1.6 0 0 1 0 3.2h-2a1.6 1.6 0 0 0 0 3.2H14.5" /></Icon>,
    title: 'When commission is released or paid',
    sub: 'Instead of checking Earnings to find out',
  },
  {
    icon: <Icon><circle cx="8" cy="9" r="3" /><circle cx="16" cy="9" r="3" /><path d="M3 19a5 5 0 0 1 10 0" /><path d="M13.5 15.5a5 5 0 0 1 7.5 3.5" /></Icon>,
    title: 'Co-share requests on your listings',
    sub: 'Another agent has a buyer and is waiting on you',
  },
];

export default function PushPrompt() {
  const { showPrompt, autoShow, manualOpen, enable, dismiss, closePrompt } = usePushPrompt();
  const { message } = AntApp.useApp();
  const { pathname } = useLocation();
  // A memo from HQ the agent must confirm comes first; the ask waits for it and
  // comes back once it is confirmed.
  const memoOpen = useMemoGateOpen();
  const [visible, setVisible] = useState(false);

  // A customer on a public signing link is not an agent, and mid-recovery is the
  // wrong moment to ask for anything.
  const suppressed = pathname.startsWith('/sign/') || pathname.startsWith('/join/') || pathname === '/reset-password' || memoOpen;

  // The automatic ask waits for the screen behind it to finish painting — landing
  // on the home screen and being handed a sheet before you have seen anything
  // reads as a toll gate. A manual open is instant; it was asked for.
  useEffect(() => {
    if (!showPrompt || suppressed) { setVisible(false); return undefined; }
    if (manualOpen) { setVisible(true); return undefined; }
    const t = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(t);
  }, [showPrompt, manualOpen, suppressed]);

  const { mounted, closing } = useSheetAnim(visible);

  const handleEnable = async () => {
    await enable();
    setVisible(false);
    message.success('Notifications are on');
  };

  // Closing a manual open just closes — it was opened on purpose, so don't re-arm
  // the 30-day dismissal against it. Closing the automatic ask records it.
  const handleClose = () => {
    setVisible(false);
    if (manualOpen) closePrompt();
    else dismiss();
  };

  if (!mounted) return null;

  return (
    <div
      onClick={handleClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ width: '100%', maxWidth: 460, margin: '0 auto', background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '10px 16px 0', flex: 'none' }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto' }} />
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3Z" />
                <line x1="10.5" y1="20.5" x2="13.5" y2="20.5" />
              </svg>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600 }}>Get notified as it happens</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>You can turn this off any time</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 8 }}>
            {REASONS.map((r) => (
              <div key={r.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ color: 'var(--primary)', flex: 'none', marginTop: 1, display: 'flex' }}>{r.icon}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{r.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4 }}>{r.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex: 'none', padding: '14px 18px calc(16px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 9, borderTop: '1px solid var(--border)' }}>
          {/* The browser's own dialog opens on this tap, so the label says what
              happens next rather than promising it is already done. */}
          <AsyncButton
            type="primary"
            size="large"
            block
            style={{ height: 46, fontWeight: 600 }}
            onClick={handleEnable}
            onError={(e) => message.error(e.message)}
          >
            Turn on notifications
          </AsyncButton>
          <Button type="text" block style={{ height: 40, color: 'var(--muted)', fontWeight: 600 }} onClick={handleClose}>
            {autoShow && !manualOpen ? 'Not now' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
